import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/ui/form'
import { Input } from '@/shared/ui/input'
import { Spinner } from '@/shared/ui/spinner'
import { Textarea } from '@/shared/ui/textarea'

// The name holds what `NameDialog` allows, so both dialogs refuse the same names.
const schema = z.object({
  name: z.string().trim().min(1).max(160),
  description: z.string().trim(),
})

export type Details = z.infer<typeof schema>

/**
 * A thing's name and the description of what it is for.
 *
 * The description is the one place a database or a view says what it holds,
 * which is what a person or an agent reads before making another one.
 *
 * Mount it only while it is open, for the reason `NameDialog` gives.
 */
export function DetailsDialog({
  open,
  onOpenChange,
  title,
  nameLabel,
  descriptionLabel,
  descriptionHint,
  current,
  pending,
  onSubmit,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  nameLabel: string
  descriptionLabel: string
  /** A line under the description saying what to write there. */
  descriptionHint: string
  current: Details
  pending: boolean
  onSubmit: (details: Details) => void
}) {
  const { t } = useTranslation()
  const form = useForm<Details>({
    resolver: zodResolver(schema),
    defaultValues: current,
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={(event) => void form.handleSubmit(onSubmit)(event)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{nameLabel}</FormLabel>
                  <FormControl>
                    <Input autoFocus {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{descriptionLabel}</FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} />
                  </FormControl>
                  <FormDescription>{descriptionHint}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                {t('action.cancel')}
              </Button>
              <Button type="submit" disabled={pending}>
                {pending && <Spinner />}
                {t('action.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
